import i18n from "@/shared/lib/i18n/index.ts";
import type {
  AlertAnalyticsOutcomeCounts,
  AlertAnalyticsPeriodReport,
  AlertAnalyticsReviewerBreakdown,
  AlertAnalyticsReviewerRole,
  AlertAnalyticsShift,
} from "@/alerts/types/alerts-analytics";

/** Translation keys for the analytics-safe reviewer roles. */
const REVIEWER_ROLE_KEYS: Record<
  AlertAnalyticsReviewerRole,
  "reviewerRoles.operator" | "reviewerRoles.supervisor" | "reviewerRoles.reviewer"
> = {
  operator: "reviewerRoles.operator",
  supervisor: "reviewerRoles.supervisor",
  reviewer: "reviewerRoles.reviewer",
};

/** Display label for an analytics-safe reviewer role; unknown codes render verbatim. */
export const reviewerRoleLabel = (role: AlertAnalyticsReviewerRole): string => {
  const key = REVIEWER_ROLE_KEYS[role];
  return key ? i18n.t(key, { ns: "alerts" }) : role;
};

/**
 * Ported from `feat/alerts-list-querying`.
 *
 * Pure view-model for the "Review activity" section. Keeps all partition and
 * fallback logic out of the component so every scenario is unit-testable.
 *
 * Business rules encoded here:
 * - Reviewer counts are INITIAL decisions; the team `outcomes` are FINAL. Never
 *   reconcile the two by code — they can differ after a correction.
 * - Identity is `principal_id` (the row key); the label is display-only with a
 *   `Reviewer #<id>` fallback.
 * - Attribution partitions `alerts_reviewed` into reviewers + other + unattributed.
 */

export interface ReviewActivityRow {
  /** React key. Always `principal_id`, never the label. */
  key: string;
  label: string;
  /** Analytics-safe role label (Operator/Supervisor/Reviewer). */
  roleLabel: string;
  reviewsCompleted: number;
  outcomes: AlertAnalyticsOutcomeCounts;
}

export interface ReviewActivityView {
  isEmpty: boolean;
  alertsReviewed: number;
  rows: ReviewActivityRow[];
  otherReviewers: {
    reviewerCount: number;
    reviewsCompleted: number;
    outcomes: AlertAnalyticsOutcomeCounts;
  } | null;
  unattributed: { reviewsCompleted: number; outcomes: AlertAnalyticsOutcomeCounts } | null;
  truncated: boolean;
  /** sum(reviewers) + other + unattributed. */
  partitionTotal: number;
  /** partitionTotal === alertsReviewed. */
  reconciles: boolean;
}

/** Display label with the required null-label fallback. */
export function reviewerLabel(principalId: string, label: string | null): string {
  return label ?? i18n.t("reviewerRoles.fallback", { ns: "alerts", id: principalId });
}

/** Initials for an avatar, derived from the display label. Null label → "#". */
export function reviewerInitials(label: string | null): string {
  if (!label) return "#";
  const parts = label.trim().split(/\s+/);
  const a = parts[0]?.[0] ?? "";
  const b = parts[1]?.[0] ?? parts[0]?.[1] ?? "";
  return (a + b).toUpperCase() || "#";
}

/** Stable identity color from principal_id (identity, never volume/ranking). */
const AVATAR_PALETTE = ["#8A6D3B", "#5B7A99", "#7A6A86", "#4E86B0", "#9A6A5B", "#5F8A6A"];
export function reviewerAvatarColor(principalId: string): string {
  let h = 0;
  for (let i = 0; i < principalId.length; i += 1) h = (h * 31 + principalId.charCodeAt(i)) >>> 0;
  return AVATAR_PALETTE[h % AVATAR_PALETTE.length];
}

/** The reviewer breakdown for a period summary, or null when there is none. */
export function reviewerBreakdownOf(
  periodSummary: AlertAnalyticsPeriodReport | null | undefined,
): AlertAnalyticsReviewerBreakdown | null {
  return periodSummary?.reviewer_breakdown ?? null;
}

export function buildReviewActivityView(
  breakdown: AlertAnalyticsReviewerBreakdown | null | undefined,
): ReviewActivityView | null {
  if (!breakdown) return null;

  const rows: ReviewActivityRow[] = breakdown.reviewers.map((r) => ({
    key: r.principal_id,
    label: reviewerLabel(r.principal_id, r.principal_label),
    roleLabel: reviewerRoleLabel(r.role_code),
    reviewsCompleted: r.reviews_completed,
    outcomes: r.initial_outcome_counts,
  }));

  const otherReviewers = breakdown.other_reviewers
    ? {
        reviewerCount: breakdown.other_reviewers.reviewer_count,
        reviewsCompleted: breakdown.other_reviewers.reviews_completed,
        outcomes: breakdown.other_reviewers.initial_outcome_counts,
      }
    : null;

  const unattributed = breakdown.unattributed
    ? {
        reviewsCompleted: breakdown.unattributed.reviews_completed,
        outcomes: breakdown.unattributed.initial_outcome_counts,
      }
    : null;

  const partitionTotal =
    breakdown.reviewers.reduce((sum, r) => sum + r.reviews_completed, 0) +
    (otherReviewers?.reviewsCompleted ?? 0) +
    (unattributed?.reviewsCompleted ?? 0);

  const isEmpty =
    breakdown.alerts_reviewed === 0 &&
    breakdown.reviewers.length === 0 &&
    otherReviewers === null &&
    unattributed === null;

  return {
    isEmpty,
    alertsReviewed: breakdown.alerts_reviewed,
    rows,
    otherReviewers,
    unattributed,
    truncated: breakdown.truncated,
    partitionTotal,
    reconciles: partitionTotal === breakdown.alerts_reviewed,
  };
}

/** Distinct people who made decisions: named reviewers + the "other" tail count. */
export function reviewersActiveCount(view: ReviewActivityView | null): number {
  if (!view) return 0;
  return view.rows.length + (view.otherReviewers?.reviewerCount ?? 0);
}

const OUTCOME_KEYS: Array<keyof AlertAnalyticsOutcomeCounts> = [
  "confirmed_signal",
  "false_alert",
  "duplicate",
  "insufficient_evidence",
  "other",
  "unclassified_legacy",
];

/** Total outcome mix across everyone in a view (for the shift-level composition bar). */
export function aggregateViewOutcomes(view: ReviewActivityView | null): AlertAnalyticsOutcomeCounts {
  const total: AlertAnalyticsOutcomeCounts = {
    confirmed_signal: 0,
    false_alert: 0,
    duplicate: 0,
    insufficient_evidence: 0,
    other: 0,
    unclassified_legacy: 0,
  };
  if (!view) return total;
  const add = (o: AlertAnalyticsOutcomeCounts) => {
    for (const key of OUTCOME_KEYS) total[key] += o[key];
  };
  view.rows.forEach((row) => add(row.outcomes));
  if (view.otherReviewers) add(view.otherReviewers.outcomes);
  if (view.unattributed) add(view.unattributed.outcomes);
  return total;
}

export interface ShiftReviewSection {
  shiftKey: string;
  label: string;
  status: string;
  startTime: string;
  endTime: string;
  /** Terminal decisions in the shift (`queue_flow.alerts_reviewed`). Null if not started. */
  reviewsCompleted: number | null;
  /** Alerts accepted in the shift (`queue_flow.alerts_received`) — coverage context. */
  received: number | null;
  /** Distinct people who reviewed in the shift. */
  reviewersActive: number;
  /** Null when the shift is not started (period_summary null) or has no reviewer data. */
  view: ReviewActivityView | null;
}

/**
 * One review section per shift (local-day mode only). This is a COVERAGE + LOAD
 * view: per shift it exposes how much was reviewed vs received and by how many
 * people — not a performance/ranking metric. A not-started shift has
 * `period_summary: null` → `view: null`. Absolute-range mode has no
 * `shift_breakdown` → returns `[]`, and the caller shows a single period block.
 */
export function buildShiftReviewSections(
  shiftBreakdown: AlertAnalyticsShift[] | null | undefined,
): ShiftReviewSection[] {
  if (!shiftBreakdown) return [];
  return shiftBreakdown.map((shift) => {
    const summary = shift.period_summary;
    const view = buildReviewActivityView(reviewerBreakdownOf(summary));
    return {
      shiftKey: shift.shift_key,
      label: shift.label,
      status: shift.status,
      startTime: shift.start_time,
      endTime: shift.end_time,
      reviewsCompleted: summary?.queue_flow?.alerts_reviewed ?? null,
      received: summary?.queue_flow?.alerts_received ?? null,
      reviewersActive: reviewersActiveCount(view),
      view,
    };
  });
}

/** Non-zero outcome entries in a stable display order (includes `duplicate`). */
const OUTCOME_ORDER: Array<keyof AlertAnalyticsOutcomeCounts> = [
  "confirmed_signal",
  "false_alert",
  "duplicate",
  "insufficient_evidence",
  "other",
  "unclassified_legacy",
];

export const OUTCOME_SHORT_LABELS: Record<keyof AlertAnalyticsOutcomeCounts, string> = {
  confirmed_signal: "Confirmed",
  false_alert: "False alert",
  duplicate: "Duplicate",
  insufficient_evidence: "Insufficient",
  other: "Other",
  unclassified_legacy: "Legacy",
};

export function nonZeroOutcomes(
  counts: AlertAnalyticsOutcomeCounts,
): Array<{ code: keyof AlertAnalyticsOutcomeCounts; count: number }> {
  return OUTCOME_ORDER.filter((code) => counts[code] > 0).map((code) => ({
    code,
    count: counts[code],
  }));
}
